/**
 * PermitPreviewPanel Component
 *
 * Shows a live preview of what the permit will look like based on current form data.
 * Helps users understand the consequences of their selections.
 *
 * Phase 9.10 - Christa UX Improvements
 */
import { cn } from '@/lib/utils';
import type { PermitCreate } from '@/types/permits';
import { getStatusInfo, getRenewalInfo, getReminderInfo } from './permitPreviewUtils';

interface PermitPreviewPanelProps {
  formData: Partial<PermitCreate>;
  applicantOrgName?: string;
  isRenewed?: boolean;
}

export function PermitPreviewPanel({
  formData,
  applicantOrgName,
  isRenewed
}: PermitPreviewPanelProps) {
  // Don't show panel until permit type is selected
  if (!formData.permit_type) {
    return null;
  }

  // Special case: renewed permit is read-only
  if (isRenewed) {
    return (
      <div
        data-testid="permit-preview-panel"
        className="bg-purple-50 border border-purple-200 rounded-lg p-4 space-y-1"
      >
        <p className="text-sm font-medium text-purple-800">
          This permit has been renewed — read-only
        </p>
        <p className="text-xs text-slate-600">
          You cannot edit a permit that has been renewed. View the new permit instead.
        </p>
      </div>
    );
  }

  const statusInfo = getStatusInfo(formData);
  const renewalInfo = getRenewalInfo(formData);
  const reminderInfo = getReminderInfo(formData, applicantOrgName);

  const hasWarning = renewalInfo.isWarning;

  return (
    <div
      data-testid="permit-preview-panel"
      className={cn(
        'rounded-lg p-4 space-y-2',
        hasWarning
          ? 'bg-orange-50 border border-orange-200'
          : 'bg-blue-50 border border-blue-200'
      )}
    >
      <p className={cn(
        'text-sm font-medium',
        hasWarning ? 'text-orange-800' : 'text-blue-800'
      )}>
        📋 What you're creating:
      </p>

      <div className="text-sm text-gray-700 space-y-1">
        {/* Status line */}
        <p>
          <span className="font-medium">Status:</span>{' '}
          {statusInfo.label} ({statusInfo.explanation})
        </p>

        {/* Renewals line */}
        <p>
          <span className="font-medium">Renewals:</span>{' '}
          {renewalInfo.isWarning && '⚠️ '}
          {renewalInfo.text}
        </p>

        {/* Reminders line */}
        <p>
          <span className="font-medium">Reminders:</span>{' '}
          {reminderInfo}
        </p>
      </div>
    </div>
  );
}
